/**
 * Link IFC Tool - BIM 2025 Grade
 *
 * Links IFC 2x3 / IFC4 / IFC4x3 files into the project as a live
 * coordination reference. The file is parsed with the web-ifc wasm engine,
 * every product mesh is baked into one IndexedDB asset and the scene draws
 * the real geometry (authored colours, world positions re-centred to the
 * element origin). A failed or empty parse rolls the link back and reports
 * the error — no schematic stand-in is ever created for an unreadable file.
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { LinkManager, LinkInstance } from '../core/LinkManager';
import { ThreeEvent } from '@react-three/fiber';
import { buildIfcLinkPayload, IfcLinkPayload } from '../services/ifc/IfcLinkService';
import { CadLinkStore } from '../services/cad/CadLinkStore';
import i18n from '../i18n';

export interface LinkIFCOptions {
    filePath: string;
    classMapping: 'Auto' | 'Custom';
    importOrigin: 'Site' | 'Project';
    levelMapping: 'Create' | 'MapExplicit';
    phaseMapping: 'Default' | 'None';
}

export interface LinkIFCGeometryData {
    linkId: string;
    origin: [number, number, number];
    scale: [number, number, number];
    rotation: [number, number, number];
    /** Real model extents in metres (renderer draws these as the footprint). */
    boxSize: [number, number, number];
    /** Baked model asset — the scene streams the real geometry from it. */
    ifc: {
        assetId: string;
        schema: string;
        productCount: number;
        source: string;
    };
}

interface LinkIFCElement extends Element {
    geometryData: LinkIFCGeometryData;
}

export class LinkIFCTool extends BaseTool {
    id = 'link_ifc';
    name = 'Link IFC';
    cursor = 'default';

    private linkManager: LinkManager;

    constructor(linkManager: LinkManager) {
        super();
        this.linkManager = linkManager;
    }

    private options: LinkIFCOptions = {
        filePath: '',
        classMapping: 'Auto',
        importOrigin: 'Site',
        levelMapping: 'Create',
        phaseMapping: 'Default'
    };

    setOptions(options: Partial<LinkIFCOptions>) {
        this.options = { ...this.options, ...options };
    }

    onActivate(): void {
        // Dialog-driven command: BIM opens a file dialog for Link IFC.
        void this.pickFile('.ifc').then(async (file) => {
            if (file) {
                await this.linkIfcFile(file);
            }
            this.exitToSelect();
        });
    }

    /**
     * Full link flow with a real File: register the reference, parse the
     * model, then bind the wrapper element so Manage Links can act on it.
     */
    public async linkIfcFile(file: File): Promise<LinkInstance | null> {
        this.options.filePath = file.name;

        const link = this.linkManager.linkFile('IFC', file.name, {
            positioning: this.options.importOrigin === 'Site' ? 'SharedCoordinates' : 'OriginToOrigin',
            size: file.size
        });

        this.notify(i18n.t('tools.notify.importing', { fileName: file.name }));
        try {
            const bytes = new Uint8Array(await file.arrayBuffer());
            const payload = await buildIfcLinkPayload(file.name, bytes);
            const element = this.createIfcRootElement(link, payload);
            this.linkManager.setDocumentId(link.id, element.id);
            // Session source so Manage Links Reload re-parses the same file.
            CadLinkStore.setSource(element.id, file, file.name);
            this.notify(
                i18n.t('tools.notify.ifcLinkParsed', { fileName: file.name, count: payload.productCount, schema: payload.schema }),
                'success'
            );
            return link;
        } catch (err) {
            // No readable model → no link. Roll the registration back so
            // Manage Links never lists a reference that renders nothing.
            this.linkManager.removeLink(link.id);
            this.notify(i18n.t('tools.notify.ifcLinkFailed', { message: (err as Error).message }), 'error');
            return null;
        }
    }

    onPointerDown(_event: ThreeEvent<PointerEvent>): void {
        // Not interactive placement
    }

    onPointerMove(_event: ThreeEvent<PointerEvent>): void { }
    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    private createIfcRootElement(link: LinkInstance, payload: IfcLinkPayload): LinkIFCElement {
        const size: [number, number, number] = [
            Math.max(payload.bbox.max[0] - payload.bbox.min[0], 1),
            Math.max(payload.bbox.max[1] - payload.bbox.min[1], 1),
            Math.max(payload.bbox.max[2] - payload.bbox.min[2], 1),
        ];

        const ifcRoot: LinkIFCElement = {
            id: uuidv4(),
            category: 'IFC Links',
            type: 'Linked IFC Model: ' + link.name,
            parameters: {
                'File Path': link.path,
                'Schema': payload.schema,
                'Products': String(payload.productCount),
                'Status': link.status
            },
            constraints: [],
            geometryData: {
                linkId: link.id,
                origin: [0, 0, 0],
                scale: [1, 1, 1],
                rotation: [0, 0, 0],
                boxSize: size,
                ifc: {
                    assetId: payload.assetId,
                    schema: payload.schema,
                    productCount: payload.productCount,
                    source: link.path,
                },
            }
        };

        this.context?.bimStore.addElement(ifcRoot as any);
        return ifcRoot;
    }
}
